import { useRef, type ChangeEvent, type ReactNode } from 'react';
import { Pencil, Trash2 } from 'lucide-react';
import { useI18n } from '../../../i18n/useI18n';
import { LiksFormFieldLabel } from '../LiksFormFieldLabel/LiksFormFieldLabel';
import { LiksPhotoPreview } from '../LiksPhotoPreview/LiksPhotoPreview';
import './LiksPhotoUpload.css';

export type LiksPhotoUploadProps = {
  src: string | null;
  isLoading?: boolean;
  placeholder: ReactNode;
  inputId: string;
  accept?: string;
  disabled?: boolean;
  onFileChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onRemove?: () => void;
  label?: ReactNode;
  error?: string | null;
  /** `user` photos fill/crop (cover); `tenant` logos fit without cropping (contain). */
  variant?: 'user' | 'tenant';
  className?: string;
};

export function LiksPhotoUpload({
  src,
  isLoading = false,
  placeholder,
  inputId,
  accept = 'image/*',
  disabled = false,
  onFileChange,
  onRemove,
  label,
  error = null,
  variant = 'user',
  className,
}: LiksPhotoUploadProps) {
  const { translate } = useI18n();
  const inputRef = useRef<HTMLInputElement>(null);
  const rootClassName = ['liks-photo-upload', className]
    .filter(Boolean)
    .join(' ');
  const imageClassName = [
    'liks-photo-upload__image',
    variant === 'tenant' ? 'liks-photo-upload__image--tenant' : null,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={rootClassName}>
      {label != null ? (
        <LiksFormFieldLabel htmlFor={inputId}>{label}</LiksFormFieldLabel>
      ) : null}
      <div className="liks-photo-upload__frame">
        <LiksPhotoPreview
          className="liks-photo-upload__preview"
          imageClassName={imageClassName}
          placeholderClassName="liks-photo-upload__placeholder"
          src={src}
          isLoading={isLoading}
          loadingLabel={translate('common.loading')}
          placeholder={placeholder}
        />
        {src && onRemove ? (
          <button
            type="button"
            className="liks-photo-upload__action liks-photo-upload__action--delete"
            aria-label={translate('common.photo.remove')}
            disabled={disabled}
            onClick={onRemove}
          >
            <Trash2
              className="liks-photo-upload__action-icon"
              strokeWidth={2}
              aria-hidden
            />
          </button>
        ) : null}
        <button
          type="button"
          className="liks-photo-upload__action liks-photo-upload__action--edit"
          aria-label={
            src
              ? translate('common.photo.change')
              : translate('common.photo.upload')
          }
          disabled={disabled}
          onClick={() => inputRef.current?.click()}
        >
          <Pencil
            className="liks-photo-upload__action-icon"
            strokeWidth={2}
            aria-hidden
          />
        </button>
        <input
          ref={inputRef}
          id={inputId}
          className="liks-photo-upload__input"
          type="file"
          accept={accept}
          onChange={onFileChange}
          disabled={disabled}
        />
      </div>
      {error ? (
        <p
          id={`${inputId}-error`}
          className="liks-text-form-field__error"
          role="alert"
        >
          {error}
        </p>
      ) : null}
    </div>
  );
}
